import { useEffect } from 'react';
import { getLenis } from '../../lib/smoothScroll';
import usePrefersReducedMotion from '../../hooks/usePrefersReducedMotion';

// Effect 6 (Task 3.11 / D-34) — scroll-velocity "seasoning", site-wide and subtle.
//
// Maps the smoothed scroll velocity (Lenis's own `velocity`, already running
// site-wide since 3.9 / D-29) to a tiny shear exposed as a CSS custom property
// `--scroll-skew` on :root. Any element carrying the `.vel-season` class leans a
// fraction of a degree into fast scrolling and eases back to flat the instant the
// visitor slows or stops — a per-frame lerp toward the target handles both the
// lean and the settle, so there's no snap and no CSS transition fighting the var.
//
// Deliberately restrained: the lean is clamped to ±MAX_SKEW (the D-34 brief caps
// this at 2–4°). This is ambient seasoning, NOT a headline effect — the opposite
// failure mode from the reticle's "too subtle" (3.9 Round 4 §4e); here staying
// genuinely light is the point.
//
// Reduced motion: Lenis isn't initialised at all (getLenis() is null), so the loop
// never leans; the var is pinned to 0deg and every `.vel-season` element sits flat.
// Mount once at the app shell.
const MAX_SKEW = 3; // degrees — within the brief's 2–4° cap
const REF_VELOCITY = 42; // Lenis velocity that maps to the full ±MAX_SKEW lean

export default function VelocitySeasoning() {
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    const root = document.documentElement;
    const reset = () => root.style.setProperty('--scroll-skew', '0deg');

    if (reduced || typeof window === 'undefined') {
      reset();
      return undefined;
    }

    let raf = 0;
    let current = 0;

    const clamp = (v) => Math.max(-MAX_SKEW, Math.min(MAX_SKEW, v));
    const loop = () => {
      const lenis = getLenis();
      const v = lenis ? lenis.velocity : 0; // px/frame, signed by direction
      const target = clamp((v / REF_VELOCITY) * MAX_SKEW);
      // Ease toward the target each frame; when velocity → 0 the target is 0, so
      // this doubles as the "settle back to flat on idle" easing.
      current += (target - current) * 0.12;
      if (Math.abs(current) < 0.005) current = 0;
      root.style.setProperty('--scroll-skew', `${current.toFixed(3)}deg`);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      reset();
    };
  }, [reduced]);

  return null;
}
